import React, { useState, useEffect } from 'react';
import { addressAPI } from '../../services/api';
import '../../styles/shared.css';
import './AddressPage.css';

interface Address {
  id: number;
  name: string;
  phone: string;
  province: string;
  city: string;
  district: string;
  detail: string;
  postal_code: string;
  is_default: boolean;
}

const AddressPage: React.FC = () => {
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState<Address | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    province: '',
    city: '',
    district: '',
    detail: '',
    postal_code: '',
    is_default: false
  });

  useEffect(() => {
    fetchAddresses();
  }, []);

  const fetchAddresses = async () => {
    try {
      setLoading(true);
      const response = await addressAPI.getMyAddresses();
      setAddresses(response);
    } catch (error) {
      console.error('获取地址失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingAddress) {
        await addressAPI.updateAddress(editingAddress.id, formData);
      } else {
        await addressAPI.addAddress(formData);
      }
      fetchAddresses();
      resetForm();
      alert('保存成功');
    } catch (error) {
      console.error('保存失败:', error);
      alert('保存失败');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('确定要删除这个地址吗？')) return;
    
    try {
      await addressAPI.deleteAddress(id);
      fetchAddresses();
    } catch (error) {
      console.error('删除失败:', error);
      alert('删除失败');
    }
  };

  const handleEdit = (address: Address) => {
    setEditingAddress(address);
    setFormData(address);
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingAddress(null);
    setFormData({
      name: '',
      phone: '',
      province: '',
      city: '',
      district: '',
      detail: '',
      postal_code: '',
      is_default: false
    });
  };

  const setDefault = async (id: number) => {
    try {
      await addressAPI.setDefaultAddress(id);
      fetchAddresses();
    } catch (error) {
      console.error('设置默认地址失败:', error);
      alert('设置默认地址失败');
    }
  };

  if (loading) return <div className="loading">加载中...</div>;

  return (
    <div className="page-container address-page">
      <div className="page-header">
        <div className="header-content">
          <h1>我的地址</h1>
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>
            新增地址
          </button>
        </div>
      </div>

      <div className="content-container">
        {showForm && (
          <div className="address-form-container">
            <form className="address-form" onSubmit={handleSubmit}>
              <h3>{editingAddress ? '编辑地址' : '新增地址'}</h3>
              
              <div className="form-group">
                <label>收货人姓名</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  placeholder="请输入收货人姓名"
                />
              </div>

              <div className="form-group">
                <label>联系电话</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  required
                  placeholder="请输入联系电话"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>省份</label>
                  <input
                    type="text"
                    value={formData.province}
                    onChange={(e) => setFormData({...formData, province: e.target.value})}
                    required
                    placeholder="请输入省份"
                  />
                </div>
                
                <div className="form-group">
                  <label>城市</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    required
                    placeholder="请输入城市"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>区县</label>
                <input
                  type="text"
                  value={formData.district}
                  onChange={(e) => setFormData({...formData, district: e.target.value})}
                  required
                  placeholder="请输入区县"
                />
              </div>

              <div className="form-group">
                <label>详细地址</label>
                <input
                  type="text"
                  value={formData.detail}
                  onChange={(e) => setFormData({...formData, detail: e.target.value})}
                  required
                  placeholder="请输入详细地址"
                />
              </div>

              <div className="form-group">
                <label>邮政编码</label>
                <input
                  type="text"
                  value={formData.postal_code}
                  onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                  placeholder="请输入邮政编码（可选）"
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_default}
                    onChange={(e) => setFormData({...formData, is_default: e.target.checked})}
                  />
                  设为默认地址
                </label>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  保存
                </button>
                <button type="button" className="btn btn-tertiary" onClick={resetForm}>
                  取消
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="address-list">
          {addresses.length === 0 ? (
            <div className="empty-state">
              <p>暂无地址信息</p>
              <button className="btn btn-primary" onClick={() => setShowForm(true)}>
                添加第一个地址
              </button>
            </div>
          ) : (
            addresses.map(address => (
              <div key={address.id} className={`address-card ${address.is_default ? 'default' : ''}`}>
                <div className="address-info">
                  <div className="address-name-phone">
                    <span className="address-name">{address.name}</span>
                    <span className="address-phone">{address.phone}</span>
                    {address.is_default && <span className="default-badge">默认</span>}
                  </div>
                  <div className="address-detail">
                    {address.province} {address.city} {address.district} {address.detail}
                    {address.postal_code && ` (${address.postal_code})`}
                  </div>
                </div>
                
                <div className="address-actions">
                  {!address.is_default && (
                    <button className="btn btn-outline btn-small" onClick={() => setDefault(address.id)}>
                      设为默认
                    </button>
                  )}
                  <button className="btn btn-outline btn-small" onClick={() => handleEdit(address)}>
                    编辑
                  </button>
                  <button className="btn btn-danger btn-small" onClick={() => handleDelete(address.id)}>
                    删除
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AddressPage;